import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import React, { PropsWithChildren } from 'react'
import { client } from '../apollo/client'
import { Layout } from '../components/layout/layout'
import { REVALIDATION_FREQUENCY } from '../constants/revalidation'
import { GlobalProps, withLayoutProps } from '../globals/layout'
import {
  NonFcpPageQuery,
  NonFcpPageQueryDocument,
  NonFcpPageQueryVariables,
  PageQuery,
  PageQueryDocument,
  PageQueryVariables,
  RetourQuery,
  RetourQueryDocument,
  RetourQueryVariables,
} from '@gql'
import { nonNull } from '../utils/nonNull'
import { pageQuery } from '../utils/pageQuery'

export type PageProps = GlobalProps & {
  data: PageQuery
}

const Fcp = dynamic(() => import('../templates/page').then(mod => mod.Page))

const Page: NextPage<PageProps> = ({ globals, data }) => {
  let Template: React.ComponentType<PropsWithChildren<typeof data>> = React.Fragment

  switch (data.entry && data.entry.__typename) {
  case 'products_pages_product_fcp_Entry':
    Template = Fcp
    break
  }

  return (
    <Layout globals={globals}>
      <Template { ...data } />
    </Layout>
  )
}

export default Page

export const getStaticProps: GetStaticProps<Record<string, unknown>, { slug: string[] }> = async (context) => {
  const slug = context.params?.slug


  if (!slug) {
    throw new Error('Missing page slug')
  }

  const retour = await client.query<RetourQuery, RetourQueryVariables>({
    query: RetourQueryDocument,
    variables: {
      uri: `/${slug.join('/')}`,
    },
  })

  const { retourResolveRedirect } = retour?.data
  
  if (retourResolveRedirect?.redirectDestUrl !== null && typeof retourResolveRedirect?.redirectDestUrl === 'string') {
    return {
      redirect: {
        destination: retourResolveRedirect?.redirectDestUrl,
        permanent: false,
      },
    }
  }

  const { data } = await pageQuery<PageQuery, PageQueryVariables>({
    query: PageQueryDocument,
    variables: {
      uri: slug.join('/'),
    },
  }, context)

  return withLayoutProps({
    path: `/${slug.join('/')}`,
  }, data.entry ? {
    props: { data },
    revalidate: REVALIDATION_FREQUENCY,
  } : {
    notFound: true,
  })
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await client.query<NonFcpPageQuery, NonFcpPageQueryVariables>({
    query: NonFcpPageQueryDocument,
  })

  const fcpPaths = ['/about-us']

  return ({
    paths: nonNull(data.entries?.map(entry => entry?.uri ? `/${entry.uri}` : undefined) ?? []).concat(fcpPaths),
    fallback: 'blocking',
  })
}
